import { Injectable } from '@angular/core';
import { LoggerService } from '../../services/logger.service';
import { WebsiteElement } from '@way/website-themes/themes/minimal_website/home-page-preview/home-page-preview.component';
import { WashPackagePanelData } from '../components/template/panels/wash-packages-panel';
import { ExpressDetailsPanelData } from '../components/template/panels/express-details-panel';
import { CouponsPanelData, CouponInstructionStep } from '../components/template/panels/coupons-panel';
import { TextEditorStyle } from '../../shared/components/text-editor';
import { FleetsPanelData } from '../components/template/panels/fleets-panel';

/**
 * Mutable context bag passed from the component into each service method.
 * Using object identity (pass-by-reference) so nested mutations to `json`
 * are reflected in the component without explicit return values.
 */
export interface SectionContext {
  /** Current page JSON data — mutated in-place. */
  json: any;
  /** Template source data — read-only. */
  templateData: any;
  /** Elements array — may be replaced with a new spread reference. */
  elements: WebsiteElement[];
  /** Panel data fields — mutated by load/change/save methods. */
  washPackagesPanelData: WashPackagePanelData;
  expressDetailsPanelData: ExpressDetailsPanelData;
  couponsPanelData: CouponsPanelData;
  fleetsPanelData: FleetsPanelData;
  /** Panel-open flags — mutated by close methods. */
  showWashPackagesPanel: boolean;
  showExpressDetailsPanel: boolean;
  showCouponsPanel: boolean;
  showFleetsPanel: boolean;
  /** Delegate for queueing autosave paths. */
  scheduleAutosave(paths: string[]): void;
}

/**
 * Manages load / onChange / onSave / close logic for the four section-panel pages:
 * Wash Packages, Express Auto Details, Coupons, and Fleet Management.
 * Methods operate on a {@link SectionContext} passed by the component.
 */
@Injectable({ providedIn: 'root' })
export class SectionPanelsService {

  constructor(private logger: LoggerService) {}
  closeWashPackagesPanel(ctx: SectionContext): void {
    ctx.showWashPackagesPanel = false;
  }

  /**
   * Loads wash packages panel data from page-level, section-level, or default values and stores it on the context.
   * @example
   * oadWashPackagesData(ctx)
   * void
   * @param {SectionContext} ctx - Section context containing JSON page data and template data to read from and update.
   * @returns {void} Does not return a value.
   */
  loadWashPackagesData(ctx: SectionContext): void {
    // Priority 1: Load from page-level (matches JSON structure: page.title, page.description, page.theme)
    // Priority 2: Load from page-title section
    // Priority 3: Fallback to defaults
    
    const pageData = ctx.json?.page || ctx.templateData?.json_data?.page || {};
    const sections = pageData.sections || ctx.templateData?.json_data?.page?.sections || {};
    const pageTitleSection = sections['page-title'] || sections.hero_section || {};
    
    // Extract title and style - Priority: page.title > page-title section > default
    let title = '';
    let titleStyle: any = null;
    if (pageData.title && typeof pageData.title === 'string') {
      title = pageData.title;
    } else if (pageData.title && typeof pageData.title === 'object' && pageData.title.label) {
      title = pageData.title.label;
      // Extract titleStyle from API format
      const titleStyleObj = pageData.title.style?.text_style;
      if (titleStyleObj && typeof titleStyleObj === 'object') {
        titleStyle = {
          fontWeight: titleStyleObj.fontWeight || null,
          fontStyle: titleStyleObj.fontStyle || null,
          textDecoration: titleStyleObj.textDecoration || null,
          textAlign: titleStyleObj.textAlign || null
        };
      }
    } else if (pageTitleSection.title) {
      if (typeof pageTitleSection.title === 'string') {
        title = pageTitleSection.title;
      } else if (pageTitleSection.title.label) {
        title = pageTitleSection.title.label;
        // Extract titleStyle from page-title section
        const titleStyleObj = pageTitleSection.title.style?.text_style;
        if (titleStyleObj && typeof titleStyleObj === 'object') {
          titleStyle = {
            fontWeight: titleStyleObj.fontWeight || null,
            fontStyle: titleStyleObj.fontStyle || null,
            textDecoration: titleStyleObj.textDecoration || null,
            textAlign: titleStyleObj.textAlign || null
          };
        }
      }
    }
    if (!title) {
      title = 'Express Wash Packages';
    }
    
    // Extract subtitle/description - Priority: page.description > page-title section > default
    let subtitle = '';
    if (pageData.description) {
      subtitle = pageData.description;
    } else {
      subtitle = pageTitleSection.description || pageTitleSection.subtitle || 'Choose from our range of washes that suit all needs and budgets.';
    }
    
    // Load theme and background color - Priority: page.theme > package_section > page-title section > default
    const packageSection = sections.package_section || {};
    let theme: 'light' | 'dark' = 'light';
    let backgroundColor = '#FFFFFF';
    
    // Check page-level theme first
    if (pageData.theme) {
      // Determine theme type from background_color
      const bgColor = pageData.theme.background_color || '';
      if (bgColor && (bgColor.includes('#1F2937') || bgColor.includes('#111827') || bgColor.includes('#000'))) {
        theme = 'dark';
      }
      backgroundColor = pageData.theme.background_color || backgroundColor;
    } else {
      // Fallback to section-level theme
      theme = packageSection.theme || pageTitleSection.theme || 'light';
      backgroundColor = packageSection.backgroundColor || pageTitleSection.backgroundColor || '#FFFFFF';
    }
    
    // Get package_section packages to load saved package settings (imageFit, buttonText)
    const packageSectionPackages = packageSection.packages || [];
    
    // Initialize with title, subtitle, theme, and empty packages array
    // The panel component will load packages from API
    ctx.washPackagesPanelData = {
      title: title,
      subtitle: subtitle,
      titleStyle: titleStyle || undefined,
      theme: theme,
      backgroundColor: backgroundColor,
      packages: [],
      packageSectionPackages: packageSectionPackages // Pass saved package settings to panel
    } as any;
    
    this.logger.debug('📦 Loaded wash packages data from page-level:', {
      title: ctx.washPackagesPanelData.title,
      subtitle: ctx.washPackagesPanelData.subtitle,
      theme: ctx.washPackagesPanelData.theme,
      backgroundColor: ctx.washPackagesPanelData.backgroundColor,
      packageSectionPackages: packageSectionPackages
    });
  }

  /**
  * Updates the wash packages panel data in the section context and synchronizes page-level and section-level JSON for live preview and autosave.
  * @example
  * nWashPackagesChange(data, ctx)
  * void
  * @param {WashPackagePanelData} data - The wash packages panel data to apply.
  * @param {SectionContext} ctx - The section context used to update JSON, elements, and autosave state.
  * @returns {void} Does not return a value.
  **/
  onWashPackagesChange(data: WashPackagePanelData, ctx: SectionContext): void {
    // Only process changes when the panel is actually open
    if (!ctx.showWashPackagesPanel) {
      return;
    }
    
    ctx.washPackagesPanelData = data;
    
    // Update currentJsonData for API sync (live update like hero panel)
    if (!ctx.json) ctx.json = {};
    if (!ctx.json.page) ctx.json.page = {};
    if (!ctx.json.page.sections) ctx.json.page.sections = {};
    
    // CRITICAL: Update page-level title, description, and theme (like hero and info sections do)
    // This matches the JSON structure: page.title, page.description, page.theme
    if (data.title) {
      ctx.json.page.title = data.title;
    }
    if (data.subtitle) {
      ctx.json.page.description = data.subtitle;
    }
    
    // Update page-level theme object
    if (!ctx.json.page.theme) {
      ctx.json.page.theme = {};
    }
    // Map theme colors based on theme type
    if (data.theme === 'dark') {
      ctx.json.page.theme.primary_color = '#1E40AF';
      ctx.json.page.theme.secondary_color = '#FFFFFF';
      ctx.json.page.theme.background_color = data.backgroundColor || '#1F2937';
    } else {
      // Light theme
      ctx.json.page.theme.primary_color = '#1E40AF';
      ctx.json.page.theme.secondary_color = '#FFFFFF';
      ctx.json.page.theme.background_color = data.backgroundColor || '#F9FAFB';
    }
    // Preserve font_family if it exists
    if (!ctx.json.page.theme.font_family) {
      ctx.json.page.theme.font_family = 'Inter, sans-serif';
    }
    
    // Update page-title section with title and subtitle
    const pageTitleKey = 'page-title';
    if (!ctx.json.page.sections[pageTitleKey]) {
      ctx.json.page.sections[pageTitleKey] = {};
    }
    const pageTitleSection = ctx.json.page.sections[pageTitleKey];
    
    // Update title with style (same as hero section)
    const existingTitle = pageTitleSection.title;
    if (data.titleStyle) {
      // Create or update title object with style
      if (typeof existingTitle === 'string') {
        // Convert string title to object with style
        pageTitleSection.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else if (existingTitle && typeof existingTitle === 'object') {
        // Update existing title object
        pageTitleSection.title = {
          ...existingTitle,
          label: data.title || '',
          style: {
            ...(existingTitle.style || {}),
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else {
        // Create new title object
        pageTitleSection.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      }
    } else {
      // No style, just update text
      if (existingTitle && typeof existingTitle === 'object' && existingTitle.label !== undefined) {
        pageTitleSection.title = {
          ...existingTitle,
          label: data.title || ''
        };
      } else if (typeof existingTitle === 'string') {
        pageTitleSection.title = data.title || '';
      } else {
        pageTitleSection.title = {
          label: data.title || '',
          style: {}
        };
      }
    }
    
    // Update description/subtitle
    pageTitleSection.description = data.subtitle || '';
    
    // Update theme and background color
    pageTitleSection.theme = data.theme || 'light';
    pageTitleSection.backgroundColor = data.backgroundColor || '#FFFFFF';
    
    // Keep active state if it exists
    if (pageTitleSection.active === undefined) {
      pageTitleSection.active = true;
    }
    
    // Also update package_section with theme, background color, and packages
    if (!ctx.json.page.sections.package_section) {
      ctx.json.page.sections.package_section = {};
    }
    const packageSection = ctx.json.page.sections.package_section;
    packageSection.theme = data.theme || 'light';
    packageSection.backgroundColor = data.backgroundColor || '#FFFFFF';
    
    // Update packages array with imageFit and buttonText
    if (!packageSection.packages) {
      packageSection.packages = [];
    }
    // Map panel packages to API format
    if (data.packages && data.packages.length > 0) {
      packageSection.packages = data.packages.map(pkg => ({
        id: pkg.packageId,
        image: pkg.imageUrl || '',
        scaletype: pkg.imageFit || 'fill', // Save imageFit as scaletype in API
        buttonText: pkg.buttonText || 'Buy Now'
      }));
    }
    
    // Update preview element for immediate visual feedback
    const packageElementIndex = ctx.elements.findIndex(e => 
      e.id === 'packages' || 
      e.type === 'packages' || 
      (e as any).originalKey === 'package_section'
    );
    if (packageElementIndex !== -1) {
      const packageElement = ctx.elements[packageElementIndex];
      // Create new content object with updated packages to ensure change detection
      const updatedContent = {
        ...packageElement.content,
        backgroundColor: data.backgroundColor,
        packages: packageSection.packages && packageSection.packages.length > 0 
          ? [...packageSection.packages] 
          : (packageElement.content as any).packages || []
      };
      // Create new element with new content reference
      const updatedElement = {
        ...packageElement,
        content: updatedContent
      };
      // Update the elements array with new references
      ctx.elements = [
        ...ctx.elements.slice(0, packageElementIndex),
        updatedElement,
        ...ctx.elements.slice(packageElementIndex + 1)
      ];
    }
    
    // Force update currentJsonData reference to trigger pageData change detection in preview
    // Create new reference for the sections object to ensure Angular detects the change
    if (ctx.json?.page?.sections) {
      ctx.json.page.sections = {
        ...ctx.json.page.sections,
        package_section: {
          ...ctx.json.page.sections.package_section,
          packages: packageSection.packages ? [...packageSection.packages] : []
        }
      };
      // Create new page reference
      ctx.json.page = {
        ...ctx.json.page,
        sections: ctx.json.page.sections
      };
      // Create new currentJsonData reference
      ctx.json = {
        ...ctx.json,
        page: ctx.json.page
      };
    }
    
    // Schedule autosave - include page-level updates
    ctx.scheduleAutosave([
      '/page/title',
      '/page/description',
      '/page/theme',
      `/page/sections/${pageTitleKey}`,
      '/page/sections/package_section'
    ]);
    
    this.logger.debug('📦 Wash packages panel change detected, updated page-level and section data');
  }

  /**
   * Saves wash package panel data into the section context, updates page-level metadata and theme, synchronizes the page-title and package sections, and triggers autosave.
   * @example
   * nWashPackagesSave(data, ctx)
   * void
   * @param {WashPackagePanelData} data - Wash package panel data to persist.
   * @param {SectionContext} ctx - Section context used to update JSON state, elements, and autosave.
   * @returns {void} This function does not return a value.
   **/
  onWashPackagesSave(data: WashPackagePanelData, ctx: SectionContext): void {
    ctx.washPackagesPanelData = data;
    
    // Update currentJsonData (same as onWashPackagesChange)
    if (!ctx.json) ctx.json = {};
    if (!ctx.json.page) ctx.json.page = {};
    if (!ctx.json.page.sections) ctx.json.page.sections = {};
    
    // CRITICAL: Update page-level title, description, and theme (like hero and info sections do)
    // This matches the JSON structure: page.title, page.description, page.theme
    if (data.title) {
      ctx.json.page.title = data.title;
    }
    if (data.subtitle) {
      ctx.json.page.description = data.subtitle;
    }
    
    // Update page-level theme object
    if (!ctx.json.page.theme) {
      ctx.json.page.theme = {};
    }
    // Map theme colors based on theme type
    if (data.theme === 'dark') {
      ctx.json.page.theme.primary_color = '#1E40AF';
      ctx.json.page.theme.secondary_color = '#FFFFFF';
      ctx.json.page.theme.background_color = data.backgroundColor || '#1F2937';
    } else {
      // Light theme
      ctx.json.page.theme.primary_color = '#1E40AF';
      ctx.json.page.theme.secondary_color = '#FFFFFF';
      ctx.json.page.theme.background_color = data.backgroundColor || '#F9FAFB';
    }
    // Preserve font_family if it exists
    if (!ctx.json.page.theme.font_family) {
      ctx.json.page.theme.font_family = 'Inter, sans-serif';
    }
    
    // Update page-title section with title and subtitle
    const pageTitleKey = 'page-title';
    if (!ctx.json.page.sections[pageTitleKey]) {
      ctx.json.page.sections[pageTitleKey] = {};
    }
    const pageTitleSection = ctx.json.page.sections[pageTitleKey];
    
    // Update title (preserve existing structure if it exists)
    const existingTitle = pageTitleSection.title;
    if (existingTitle && typeof existingTitle === 'object' && existingTitle.label) {
      pageTitleSection.title = {
        ...existingTitle,
        label: data.title || ''
      };
    } else if (typeof existingTitle === 'string') {
      pageTitleSection.title = {
        label: data.title || '',
        style: {}
      };
    } else {
      pageTitleSection.title = {
        label: data.title || '',
        style: {}
      };
    }
    
    // Update description/subtitle
    pageTitleSection.description = data.subtitle || '';
    
    // Update theme and background color
    pageTitleSection.theme = data.theme || 'light';
    pageTitleSection.backgroundColor = data.backgroundColor || '#FFFFFF';
    
    // Keep active state if it exists
    if (pageTitleSection.active === undefined) {
      pageTitleSection.active = true;
    }
    
    // Also update package_section with theme, background color, and packages
    if (!ctx.json.page.sections.package_section) {
      ctx.json.page.sections.package_section = {};
    }
    const packageSection = ctx.json.page.sections.package_section;
    packageSection.theme = data.theme || 'light';
    packageSection.backgroundColor = data.backgroundColor || '#FFFFFF';
    
    // Update packages array with imageFit and buttonText
    if (!packageSection.packages) {
      packageSection.packages = [];
    }
    // Map panel packages to API format
    if (data.packages && data.packages.length > 0) {
      packageSection.packages = data.packages.map(pkg => ({
        id: pkg.packageId,
        image: pkg.imageUrl || '',
        scaletype: pkg.imageFit || 'fill', // Save imageFit as scaletype in API
        buttonText: pkg.buttonText || 'Buy Now'
      }));
    }
    
    // Update preview element for immediate visual feedback
    const packageElement = ctx.elements.find(e => 
      e.id === 'packages' || 
      e.type === 'packages' || 
      (e as any).originalKey === 'package_section'
    );
    if (packageElement) {
      (packageElement.content as any).backgroundColor = data.backgroundColor;
      // Force change detection
      ctx.elements = [...ctx.elements];
    }
    
    // Trigger autosave - include page-level updates
    ctx.scheduleAutosave([
      '/page/title',
      '/page/description',
      '/page/theme',
      `/page/sections/${pageTitleKey}`,
      '/page/sections/package_section'
    ]);
    
    this.logger.debug('💾 Wash packages panel SAVE triggered:', data);
    this.logger.debug('💾 Updated page-level data:', {
      title: ctx.json.page.title,
      description: ctx.json.page.description,
      theme: ctx.json.page.theme
    });
  }

  closeExpressDetailsPanel(ctx: SectionContext): void {
    ctx.showExpressDetailsPanel = false;
  }

  /**
   * Loads express details panel data from page-level, section-level, or default values and assigns it to the section context.
   * @example
   * loadExpressDetailsData(ctx)
   * void
   * @param {SectionContext} ctx - Section context containing page JSON, template data, and the target express details panel data object.
   * @returns {void} This function does not return a value.
   */
  loadExpressDetailsData(ctx: SectionContext): void {
    // Priority 1: Load from page-level (matches JSON structure: page.title, page.description, page.theme)
    // Priority 2: Load from page-title section
    // Priority 3: Fallback to defaults
    
    const pageData = ctx.json?.page || ctx.templateData?.json_data?.page || {};
    const sections = pageData.sections || ctx.templateData?.json_data?.page?.sections || {};
    const pageTitleSection = sections['page-title'] || sections.hero_section || {};
    
    // Extract title and style - Priority: page.title > page-title section > default
    let title = '';
    let titleStyle: any = null;
    if (pageData.title && typeof pageData.title === 'string') {
      title = pageData.title;
    } else if (pageData.title && typeof pageData.title === 'object' && pageData.title.label) {
      title = pageData.title.label;
      // Extract titleStyle from API format
      const titleStyleObj = pageData.title.style?.text_style;
      if (titleStyleObj && typeof titleStyleObj === 'object') {
        titleStyle = {
          fontWeight: titleStyleObj.fontWeight || null,
          fontStyle: titleStyleObj.fontStyle || null,
          textDecoration: titleStyleObj.textDecoration || null,
          textAlign: titleStyleObj.textAlign || null
        };
      }
    } else if (pageTitleSection.title) {
      if (typeof pageTitleSection.title === 'string') {
        title = pageTitleSection.title;
      } else if (pageTitleSection.title.label) {
        title = pageTitleSection.title.label;
        // Extract titleStyle from page-title section
        const titleStyleObj = pageTitleSection.title.style?.text_style;
        if (titleStyleObj && typeof titleStyleObj === 'object') {
          titleStyle = {
            fontWeight: titleStyleObj.fontWeight || null,
            fontStyle: titleStyleObj.fontStyle || null,
            textDecoration: titleStyleObj.textDecoration || null,
            textAlign: titleStyleObj.textAlign || null
          };
        }
      }
    }
    if (!title) {
      title = 'Express Auto Details – Shine in No Time!';
    }
    
    // Extract subtitle/description - Priority: page.description > page-title section > default
    let subtitle = '';
    if (pageData.description) {
      subtitle = pageData.description;
    } else {
      subtitle = pageTitleSection.description || pageTitleSection.subtitle || 'Whether it\'s a quick clean or a full headlamp refresh, our Express Detail Packages give your ride that \'like-new\' look without the wait.';
    }
    
    // Load theme and background color - Priority: page.theme > services_section > page-title section > default
    const servicesSection = sections.services_section || {};
    let theme: 'light' | 'dark' = 'light';
    let backgroundColor = '#FFFFFF';
    
    // Check page-level theme first
    if (pageData.theme) {
      const bgColor = pageData.theme.background_color || '';
      if (bgColor && (bgColor.includes('#1F2937') || bgColor.includes('#111827') || bgColor.includes('#000'))) {
        theme = 'dark';
      }
      backgroundColor = pageData.theme.background_color || backgroundColor;
    } else {
      // Fallback to section-level theme
      theme = servicesSection.theme || pageTitleSection.theme || 'light';
      backgroundColor = servicesSection.backgroundColor || pageTitleSection.backgroundColor || '#FFFFFF';
    }
    
    // Get services_section packages to load saved package settings (imageFit, buttonText)
    const servicesSectionPackages = servicesSection.packages || [];
    
    // Initialize with title, subtitle, theme, and empty packages array
    // The panel component will load packages from API
    ctx.expressDetailsPanelData = {
      title: title,
      subtitle: subtitle,
      titleStyle: titleStyle || undefined,
      theme: theme,
      backgroundColor: backgroundColor,
      packages: [],
      servicesSectionPackages: servicesSectionPackages // Pass saved package settings to panel
    } as any;
    
    this.logger.debug('✨ Loaded express details data from page-level:', {
      title: ctx.expressDetailsPanelData.title,
      subtitle: ctx.expressDetailsPanelData.subtitle,
      theme: ctx.expressDetailsPanelData.theme,
      backgroundColor: ctx.expressDetailsPanelData.backgroundColor
    });
  }

  /**
   * Handles live updates from the express details panel and synchronizes them into the page-level and section-level section data, theme, and preview state.
   * @example
   * nExpressDetailsChange(data, ctx)
   * undefined
   * @param {ExpressDetailsPanelData} data - The updated express details panel data to apply.
   * @param {SectionContext} ctx - The current section context used to update page data, elements, and autosave state.
   * @returns {void} Does not return a value.
   */
  onExpressDetailsChange(data: ExpressDetailsPanelData, ctx: SectionContext): void {
    // Only process changes when the panel is actually open
    if (!ctx.showExpressDetailsPanel) {
      return;
    }
    
    ctx.expressDetailsPanelData = data;
    
    // Update currentJsonData for API sync (live update like hero panel)
    if (!ctx.json) ctx.json = {};
    if (!ctx.json.page) ctx.json.page = {};
    if (!ctx.json.page.sections) ctx.json.page.sections = {};
    
    // CRITICAL: Update page-level title, description, and theme (like wash packages)
    // Update page-level title with style
    if (data.titleStyle) {
      const existingPageTitle = ctx.json.page.title;
      if (typeof existingPageTitle === 'string') {
        ctx.json.page.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else if (existingPageTitle && typeof existingPageTitle === 'object') {
        ctx.json.page.title = {
          ...existingPageTitle,
          label: data.title || '',
          style: {
            ...(existingPageTitle.style || {}),
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else {
        ctx.json.page.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      }
    } else if (data.title) {
      ctx.json.page.title = data.title;
    }
    if (data.subtitle) {
      ctx.json.page.description = data.subtitle;
    }
    
    // Update page-level theme object
    if (!ctx.json.page.theme) {
      ctx.json.page.theme = {};
    }
    if (data.theme === 'dark') {
      ctx.json.page.theme.primary_color = '#1E40AF';
      ctx.json.page.theme.secondary_color = '#FFFFFF';
      ctx.json.page.theme.background_color = data.backgroundColor || '#1F2937';
    } else {
      ctx.json.page.theme.primary_color = '#1E40AF';
      ctx.json.page.theme.secondary_color = '#FFFFFF';
      ctx.json.page.theme.background_color = data.backgroundColor || '#F9FAFB';
    }
    if (!ctx.json.page.theme.font_family) {
      ctx.json.page.theme.font_family = 'Inter, sans-serif';
    }
    
    // Update page-title section (NOT hero_section - express details uses page-title)
    const pageTitleKey = 'page-title';
    if (!ctx.json.page.sections[pageTitleKey]) {
      ctx.json.page.sections[pageTitleKey] = {};
    }
    const pageTitleSection = ctx.json.page.sections[pageTitleKey];
    
    // Update title with style (same as hero section)
    const existingTitle = pageTitleSection.title;
    if (data.titleStyle) {
      // Create or update title object with style
      if (typeof existingTitle === 'string') {
        pageTitleSection.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else if (existingTitle && typeof existingTitle === 'object') {
        pageTitleSection.title = {
          ...existingTitle,
          label: data.title || '',
          style: {
            ...(existingTitle.style || {}),
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else {
        pageTitleSection.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      }
    } else {
      // No style, just update text
      if (existingTitle && typeof existingTitle === 'object' && existingTitle.label !== undefined) {
        pageTitleSection.title = {
          ...existingTitle,
          label: data.title || ''
        };
      } else if (typeof existingTitle === 'string') {
        pageTitleSection.title = data.title || '';
      } else {
        pageTitleSection.title = {
          label: data.title || '',
          style: {}
        };
      }
    }
    
    // Update description/subtitle
    pageTitleSection.description = data.subtitle || '';
    
    // Update theme and background color
    pageTitleSection.theme = data.theme || 'light';
    pageTitleSection.backgroundColor = data.backgroundColor || '#FFFFFF';
    
    // Keep active state if it exists
    if (pageTitleSection.active === undefined) {
      pageTitleSection.active = true;
    }
    
    // Also update services_section with theme, background color, and packages
    if (!ctx.json.page.sections.services_section) {
      ctx.json.page.sections.services_section = {};
    }
    const servicesSection = ctx.json.page.sections.services_section;
    servicesSection.theme = data.theme || 'light';
    servicesSection.backgroundColor = data.backgroundColor || '#FFFFFF';
    
    // Update packages array with imageFit and buttonText
    if (!servicesSection.packages) {
      servicesSection.packages = [];
    }
    // Map panel packages to API format
    if (data.packages && data.packages.length > 0) {
      servicesSection.packages = data.packages.map(pkg => ({
        id: pkg.packageId,
        image: pkg.imageUrl || '',
        scaletype: pkg.imageFit || 'fill', // Save imageFit as scaletype in API
        buttonText: pkg.buttonText || 'Book Appointment'
      }));
    }
    
    // Update preview element for immediate visual feedback
    const servicesElementIndex = ctx.elements.findIndex(e => 
      e.id === 'express-details' || 
      e.type === 'express-details' || 
      (e as any).originalKey === 'services_section'
    );
    if (servicesElementIndex !== -1) {
      const servicesElement = ctx.elements[servicesElementIndex];
      // Create new content object with updated packages to ensure change detection
      const updatedContent = {
        ...servicesElement.content,
        backgroundColor: data.backgroundColor,
        packages: servicesSection.packages && servicesSection.packages.length > 0 
          ? [...servicesSection.packages] 
          : (servicesElement.content as any).packages || []
      };
      // Create new element with new content reference
      const updatedElement = {
        ...servicesElement,
        content: updatedContent
      };
      // Update the elements array with new references
      ctx.elements = [
        ...ctx.elements.slice(0, servicesElementIndex),
        updatedElement,
        ...ctx.elements.slice(servicesElementIndex + 1)
      ];
    }
    
    // Schedule autosave - include page-level updates
    ctx.scheduleAutosave([
      '/page/title',
      '/page/description',
      '/page/theme',
      `/page/sections/${pageTitleKey}`,
      '/page/sections/services_section'
    ]);
    
    this.logger.debug('✨ Express details panel change detected, updated page-level and section data');
  }

  /**
   * Saves express details panel data into the current page JSON, updating page title, description, theme, and services section packages.
   * @example
   * nExpressDetailsSave(data, ctx)
   * undefined
   * @param {ExpressDetailsPanelData} data - Express details panel data to persist.
   * @param {SectionContext} ctx - Section context containing the page JSON and autosave scheduler.
   * @returns {void} This function does not return a value.
   **/
  onExpressDetailsSave(data: ExpressDetailsPanelData, ctx: SectionContext): void {
    ctx.expressDetailsPanelData = data;
    
    // Update currentJsonData (same as onExpressDetailsChange)
    if (!ctx.json) ctx.json = {};
    if (!ctx.json.page) ctx.json.page = {};
    if (!ctx.json.page.sections) ctx.json.page.sections = {};
    
    // CRITICAL: Update page-level title, description, and theme (like wash packages)
    // Update page-level title with style
    if (data.titleStyle) {
      const existingPageTitle = ctx.json.page.title;
      if (typeof existingPageTitle === 'string') {
        ctx.json.page.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else if (existingPageTitle && typeof existingPageTitle === 'object') {
        ctx.json.page.title = {
          ...existingPageTitle,
          label: data.title || '',
          style: {
            ...(existingPageTitle.style || {}),
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else {
        ctx.json.page.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      }
    } else if (data.title) {
      ctx.json.page.title = data.title;
    }
    if (data.subtitle) {
      ctx.json.page.description = data.subtitle;
    }
    
    // Update page-level theme object
    if (!ctx.json.page.theme) {
      ctx.json.page.theme = {};
    }
    if (data.theme === 'dark') {
      ctx.json.page.theme.primary_color = '#1E40AF';
      ctx.json.page.theme.secondary_color = '#FFFFFF';
      ctx.json.page.theme.background_color = data.backgroundColor || '#1F2937';
    } else {
      ctx.json.page.theme.primary_color = '#1E40AF';
      ctx.json.page.theme.secondary_color = '#FFFFFF';
      ctx.json.page.theme.background_color = data.backgroundColor || '#F9FAFB';
    }
    if (!ctx.json.page.theme.font_family) {
      ctx.json.page.theme.font_family = 'Inter, sans-serif';
    }
    
    // Update page-title section (NOT hero_section - express details uses page-title)
    const pageTitleKey = 'page-title';
    if (!ctx.json.page.sections[pageTitleKey]) {
      ctx.json.page.sections[pageTitleKey] = {};
    }
    const pageTitleSection = ctx.json.page.sections[pageTitleKey];
    
    // Update title with style (same as hero section)
    const existingTitle = pageTitleSection.title;
    if (data.titleStyle) {
      // Create or update title object with style
      if (typeof existingTitle === 'string') {
        pageTitleSection.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else if (existingTitle && typeof existingTitle === 'object') {
        pageTitleSection.title = {
          ...existingTitle,
          label: data.title || '',
          style: {
            ...(existingTitle.style || {}),
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else {
        pageTitleSection.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      }
    } else {
      // No style, just update text
      if (existingTitle && typeof existingTitle === 'object' && existingTitle.label !== undefined) {
        pageTitleSection.title = {
          ...existingTitle,
          label: data.title || ''
        };
      } else if (typeof existingTitle === 'string') {
        pageTitleSection.title = data.title || '';
      } else {
        pageTitleSection.title = {
          label: data.title || '',
          style: {}
        };
      }
    }
    
    // Update description/subtitle
    pageTitleSection.description = data.subtitle || '';
    
    // Update theme and background color
    pageTitleSection.theme = data.theme || 'light';
    pageTitleSection.backgroundColor = data.backgroundColor || '#FFFFFF';
    
    // Keep active state if it exists
    if (pageTitleSection.active === undefined) {
      pageTitleSection.active = true;
    }
    
    // Also update services_section with theme, background color, and packages
    if (!ctx.json.page.sections.services_section) {
      ctx.json.page.sections.services_section = {};
    }
    const servicesSection = ctx.json.page.sections.services_section;
    servicesSection.theme = data.theme || 'light';
    servicesSection.backgroundColor = data.backgroundColor || '#FFFFFF';
    
    // Update packages array with imageFit and buttonText
    if (!servicesSection.packages) {
      servicesSection.packages = [];
    }
    // Map panel packages to API format
    if (data.packages && data.packages.length > 0) {
      servicesSection.packages = data.packages.map(pkg => ({
        id: pkg.packageId,
        image: pkg.imageUrl || '',
        scaletype: pkg.imageFit || 'fill', // Save imageFit as scaletype in API
        buttonText: pkg.buttonText || 'Buy Now'
      }));
    }
    
    // Trigger autosave - include page-level updates
    ctx.scheduleAutosave([
      '/page/title',
      '/page/description',
      '/page/theme',
      `/page/sections/${pageTitleKey}`,
      '/page/sections/services_section'
    ]);
    
    this.logger.debug('💾 Express details panel SAVE triggered:', data);
    this.logger.debug('💾 Updated page-level data:', {
      title: ctx.json.page.title,
      description: ctx.json.page.description,
      theme: ctx.json.page.theme
    });
  }

  closeCouponsPanel(ctx: SectionContext): void {
    ctx.showCouponsPanel = false;
  }

  /**
  * Loads coupons panel data from page and section structures, supporting both new and legacy API formats.
  * @example
  * oadCouponsData(ctx)
  * undefined
  * @param {SectionContext} ctx - Section context containing page JSON, template data, and output storage for coupons panel data.
  * @returns {void} No return value; populates ctx.couponsPanelData with coupons page display data.
  **/
  loadCouponsData(ctx: SectionContext): void {
    const pageData = ctx.json?.page || ctx.templateData?.json_data?.page || {};
    const sections = pageData.sections || {};

    let heroSection: any = null;
    if (Array.isArray(sections)) {
      heroSection = sections.find((s: any) =>
        s.section_id === 'hero' || s.section_type === 'hero_section'
      );
    } else if (sections.hero_section) {
      heroSection = sections.hero_section;
    }

    const couponSection = sections.coupon_section || pageData.section?.coupon || {};
    const couponCard = couponSection.coupon_card || couponSection.coupon || {};
    const instructionSection =
      sections.instruction_section ||
      sections.instructions_section ||
      pageData.instructions ||
      {};

    let title = '';
    let titleStyle: TextEditorStyle | undefined;
    if (heroSection?.heading?.text) {
      title = heroSection.heading.text;
      titleStyle = this.extractCouponsHeadingStyle(heroSection.heading.style);
    } else if (heroSection?.content?.heading?.text) {
      title = heroSection.content.heading.text;
      titleStyle = this.extractCouponsHeadingStyle(heroSection.content.heading.style);
    } else if (pageData.section?.content?.heading?.text) {
      title = pageData.section.content.heading.text;
      titleStyle = this.extractCouponsHeadingStyle(pageData.section.content.heading.style);
    } else if (pageData.title && typeof pageData.title === 'object' && pageData.title.label) {
      title = pageData.title.label;
      titleStyle = this.extractCouponsHeadingStyle(pageData.title.style?.text_style);
    } else if (typeof pageData.title === 'string') {
      title = pageData.title;
    }

    let subtitle = '';
    if (heroSection?.paragraph?.text) {
      subtitle = heroSection.paragraph.text;
    } else if (heroSection?.content?.subheading?.text) {
      subtitle = heroSection.content.subheading.text;
    } else if (pageData.section?.content?.paragraph?.text) {
      subtitle = pageData.section.content.paragraph.text;
    } else if (pageData.description) {
      subtitle = pageData.description;
    }

    const promoCodeLabel = couponCard.promo_label?.text || 'PROMO CODE';
    const promoCode =
      couponCard.promo_code?.text ||
      couponCard.promoCode?.display ||
      couponCard.promoCode?.text ||
      couponCard.promoCode ||
      '';
    const availabilityLabel =
      couponCard.availability_label?.text ||
      couponCard.promoCode?.note ||
      'Available for the following washes';

    let washPackages: string[] = [];
    if (couponCard.applicable_washes?.packages && Array.isArray(couponCard.applicable_washes.packages)) {
      washPackages = couponCard.applicable_washes.packages
        .map((pkg: any) => (typeof pkg === 'string' ? pkg : pkg?.name))
        .filter((name: string) => !!name);
    } else if (Array.isArray(couponSection.categories)) {
      washPackages = couponSection.categories
        .map((cat: any) => (typeof cat === 'string' ? cat : cat?.name))
        .filter((name: string) => !!name);
    }

    let instructionHeading = '';
    if (instructionSection.heading?.text) {
      instructionHeading = instructionSection.heading.text;
    } else if (typeof instructionSection.title === 'string') {
      instructionHeading = instructionSection.title;
    } else if (instructionSection.title?.label) {
      instructionHeading = instructionSection.title.label;
    }

    let instructionSteps: CouponInstructionStep[] = [];
    if (Array.isArray(instructionSection.steps)) {
      instructionSteps = instructionSection.steps.map((step: any, index: number) => ({
        stepNumber: step.step_number || step.number || index + 1,
        title: step.title || '',
        description: step.description || ''
      }));
    }

    ctx.couponsPanelData = {
      title,
      subtitle,
      titleStyle,
      promoCodeLabel,
      promoCode: typeof promoCode === 'string' ? promoCode : String(promoCode || ''),
      availabilityLabel,
      washPackages,
      instructionHeading,
      instructionSteps
    };

    this.logger.debug('🎟️ Loaded coupons panel data:', ctx.couponsPanelData);
  }

  private extractCouponsHeadingStyle(style: any): TextEditorStyle | undefined {
    if (!style || typeof style !== 'object') {
      return undefined;
    }
    return {
      fontWeight: style.fontWeight || style.font_weight || null,
      fontStyle: style.fontStyle || style.font_style || null,
      textDecoration: style.textDecoration || style.text_decoration || null,
      textAlign: style.textAlign || style.text_align || style.alignment || null
    };
  }

  /**
  * Updates the coupons panel data and synchronizes it with the page JSON structure, including the hero section, legacy coupon section, theme, and instructions, while triggering autosave when the panel is open.
  * @example
  * nCouponsChange(data, ctx)
  * void
  * @param {CouponsPanelData} data - Updated coupons panel content and styling values.
  * @param {SectionContext} ctx - Section context containing panel state, page JSON, and autosave controls.
  * @returns {void} Does not return a value.
  **/
  onCouponsChange(data: CouponsPanelData, ctx: SectionContext): void {
    if (!ctx.showCouponsPanel) {
      return;
    }

    ctx.couponsPanelData = data;

    if (!ctx.json) ctx.json = {};
    if (!ctx.json.page) ctx.json.page = {};
    if (!ctx.json.page.sections) ctx.json.page.sections = {};

    const sections = ctx.json.page.sections;
    const heroSection = sections.hero_section || {};
    sections.hero_section = heroSection;

    heroSection.section_type = heroSection.section_type || 'heading_paragraph';
    heroSection.active = true;
    heroSection.heading = {
      ...(heroSection.heading || {}),
      text: data.title || '',
      style: {
        ...(heroSection.heading?.style || {}),
        text_color: heroSection.heading?.style?.text_color || '#2563EB',
        font_weight: data.titleStyle?.fontWeight || heroSection.heading?.style?.font_weight || '700',
        text_align: data.titleStyle?.textAlign || heroSection.heading?.style?.text_align || 'center'
      }
    };
    heroSection.paragraph = {
      ...(heroSection.paragraph || {}),
      text: data.subtitle || '',
      style: heroSection.paragraph?.style || {}
    };

    // Champs / legacy nested content shape
    if (!heroSection.content) heroSection.content = {};
    heroSection.content.heading = {
      text: data.title || '',
      style: {
        ...(heroSection.content.heading?.style || {}),
        fontWeight: data.titleStyle?.fontWeight || null,
        fontStyle: data.titleStyle?.fontStyle || null,
        textDecoration: data.titleStyle?.textDecoration || null,
        textAlign: data.titleStyle?.textAlign || 'center',
        alignment: data.titleStyle?.textAlign || 'center'
      }
    };
    heroSection.content.subheading = {
      text: data.subtitle || '',
      style: heroSection.content.subheading?.style || {}
    };

    const couponSection = sections.coupon_section || {};
    sections.coupon_section = couponSection;
    couponSection.section_type = couponSection.section_type || 'coupon_display';
    couponSection.active = true;

    if (!couponSection.coupon_card) couponSection.coupon_card = {};
    const couponCard = couponSection.coupon_card;
    couponCard.promo_label = {
      ...(couponCard.promo_label || {}),
      text: data.promoCodeLabel || 'PROMO CODE'
    };
    couponCard.promo_code = {
      ...(couponCard.promo_code || {}),
      text: data.promoCode || ''
    };
    couponCard.availability_label = {
      ...(couponCard.availability_label || {}),
      text: data.availabilityLabel || 'Available for the following washes'
    };
    couponCard.applicable_washes = {
      ...(couponCard.applicable_washes || {}),
      packages: (data.washPackages || [])
        .map(name => name.trim())
        .filter(Boolean)
        .map(name => ({
          name,
          style: {
            color: '#475569',
            font_size: '14px',
            font_weight: '600',
            text_transform: 'uppercase'
          }
        }))
    };

    const instructionSection = sections.instruction_section || {};
    sections.instruction_section = instructionSection;
    instructionSection.section_type = instructionSection.section_type || 'instructions';
    instructionSection.active = true;
    instructionSection.heading = {
      ...(instructionSection.heading || {}),
      text: data.instructionHeading || ''
    };
    instructionSection.steps = (data.instructionSteps || []).map((step, index) => ({
      step_number: step.stepNumber || index + 1,
      title: step.title || '',
      description: step.description || '',
      style: {
        title_color: '#1E293B',
        title_font_size: '18px',
        title_font_weight: '600',
        description_color: '#64748B',
        description_font_size: '15px',
        margin_bottom: index === (data.instructionSteps?.length || 0) - 1 ? '0' : '25px'
      }
    }));

    ctx.json.page.description = data.subtitle || ctx.json.page.description || '';
    if (data.title) {
      ctx.json.page.title = data.title;
    }

    ctx.json = {
      ...ctx.json,
      page: {
        ...ctx.json.page,
        sections: {
          ...ctx.json.page.sections,
          hero_section: { ...sections.hero_section },
          coupon_section: { ...sections.coupon_section },
          instruction_section: { ...sections.instruction_section }
        }
      }
    };

    ctx.scheduleAutosave([
      '/page/title',
      '/page/description',
      '/page/sections/hero_section',
      '/page/sections/coupon_section',
      '/page/sections/instruction_section'
    ]);

    this.logger.debug('🎟️ Coupons panel updated:', {
      hero: sections.hero_section,
      coupon: sections.coupon_section,
      instruction: sections.instruction_section
    });
  }

  /**
   * Saves coupon panel data into the page and section structure, updates title, subtitle, coupon color, and page theme, then schedules an autosave.
   * @example
   * nCouponsSave(data, ctx)
   * void
   * @param {CouponsPanelData} data - Coupon panel data containing title, subtitle, style, and color settings.
   * @param {SectionContext} ctx - Section context containing the current page JSON and autosave scheduler.
   * @returns {void} Does not return a value.
   */
  onCouponsSave(data: CouponsPanelData, ctx: SectionContext): void {
    // Use the same logic as onCouponsChange
    this.onCouponsChange(data, ctx);
    
    // Additional save-specific logic can go here if needed
    const pageData = ctx.json?.page || {};
    const couponSection = pageData.section;
    
    // Update section.id and title
    couponSection.id = 'coupons';
    couponSection.title = 'Coupons Section';
    
    // Update section.content.heading.text (this is the main title)
    if (!couponSection.content) {
      couponSection.content = {};
    }
    if (!couponSection.content.heading) {
      couponSection.content.heading = { text: '', style: {} };
    }
    couponSection.content.heading.text = data.title || '';
    // Update heading style with titleStyle if provided
    if (data.titleStyle) {
      couponSection.content.heading.style = {
        ...(couponSection.content.heading.style || {}),
        color: '#1B5BFF',
        alignment: data.titleStyle.textAlign || 'center',
        fontWeight: data.titleStyle.fontWeight || null,
        fontStyle: data.titleStyle.fontStyle || null,
        textDecoration: data.titleStyle.textDecoration || null,
        textAlign: data.titleStyle.textAlign || 'center'
      };
    } else {
      couponSection.content.heading.style = {
        color: '#1B5BFF',
        alignment: 'center'
      };
    }
    
    // Update section.content.paragraph.text (this is the subtitle/description)
    if (!couponSection.content.paragraph) {
      couponSection.content.paragraph = { text: '' };
    }
    couponSection.content.paragraph.text = data.subtitle || '';
    
    // Update section.coupon.promoCode.tagColor (coupon color)
    if (!couponSection.coupon) {
      couponSection.coupon = {};
    }
    if (!couponSection.coupon.promoCode) {
      couponSection.coupon.promoCode = {};
    }
    couponSection.coupon.promoCode.tagColor = data.couponColor || '#1B5BFF';
    
    // Also update page-level title and description for consistency
    // Update page-level title with style
    if (data.titleStyle) {
      const existingPageTitle = ctx.json.page.title;
      if (typeof existingPageTitle === 'string') {
        ctx.json.page.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else if (existingPageTitle && typeof existingPageTitle === 'object') {
        ctx.json.page.title = {
          ...existingPageTitle,
          label: data.title || '',
          style: {
            ...(existingPageTitle.style || {}),
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else {
        ctx.json.page.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      }
    } else if (data.title) {
      ctx.json.page.title = data.title;
    }
    if (data.subtitle) {
      ctx.json.page.description = data.subtitle;
    }
    
    // Update page-level theme object
    if (!ctx.json.page.theme) {
      ctx.json.page.theme = {};
    }
    ctx.json.page.theme.primary_color = '#1E40AF';
    ctx.json.page.theme.secondary_color = '#FFFFFF';
    ctx.json.page.theme.background_color = '#F9FAFB';
    if (!ctx.json.page.theme.font_family) {
      ctx.json.page.theme.font_family = 'Inter, sans-serif';
    }
    
    // Trigger autosave - include page-level and section updates
    ctx.scheduleAutosave([
      '/page/title',
      '/page/description',
      '/page/theme',
      '/page/section'
    ]);
    
    this.logger.debug('💾 Coupons panel SAVE triggered:', data);
    this.logger.debug('💾 Updated page.section structure:', couponSection);
    this.logger.debug('💾 Updated page-level data:', {
      title: ctx.json.page.title,
      description: ctx.json.page.description,
      theme: ctx.json.page.theme
    });
  }

  closeFleetsPanel(ctx: SectionContext): void {
    ctx.showFleetsPanel = false;
  }

  /**
   * Loads and normalizes fleet panel content from page-level, section-level, or fallback defaults.
   * @example
   * oadFleetsData(ctx)
   * undefined
   * @param {SectionContext} ctx - Section context containing JSON page data and template data used to populate the fleets panel.
   * @returns {void} No return value; populates `ctx.fleetsPanelData` with title, subtitle, theme, button text, and background color.
   */
  loadFleetsData(ctx: SectionContext): void {
    // Priority 1: Load from page-level (matches JSON structure: page.title, page.description, page.theme)
    // Priority 2: Load from page-title section
    // Priority 3: Fallback to defaults
    
    const pageData = ctx.json?.page || ctx.templateData?.json_data?.page || {};
    const sections = pageData.sections || ctx.templateData?.json_data?.page?.sections || {};
    const pageTitleSection = sections['page-title'] || sections.hero_section || {};
    const formSection = sections.form_section || {};
    
    // Extract title and style - Priority: page.title > page-title section > default
    let title = '';
    let titleStyle: any = null;
    if (pageData.title && typeof pageData.title === 'string') {
      title = pageData.title;
    } else if (pageData.title && typeof pageData.title === 'object' && pageData.title.label) {
      title = pageData.title.label;
      // Extract titleStyle from API format
      const titleStyleObj = pageData.title.style?.text_style;
      if (titleStyleObj && typeof titleStyleObj === 'object') {
        titleStyle = {
          fontWeight: titleStyleObj.fontWeight || null,
          fontStyle: titleStyleObj.fontStyle || null,
          textDecoration: titleStyleObj.textDecoration || null,
          textAlign: titleStyleObj.textAlign || null
        };
      }
    } else if (pageTitleSection.title) {
      if (typeof pageTitleSection.title === 'string') {
        title = pageTitleSection.title;
      } else if (pageTitleSection.title.label) {
        title = pageTitleSection.title.label;
        // Extract titleStyle from page-title section
        const titleStyleObj = pageTitleSection.title.style?.text_style;
        if (titleStyleObj && typeof titleStyleObj === 'object') {
          titleStyle = {
            fontWeight: titleStyleObj.fontWeight || null,
            fontStyle: titleStyleObj.fontStyle || null,
            textDecoration: titleStyleObj.textDecoration || null,
            textAlign: titleStyleObj.textAlign || null
          };
        }
      }
    }
    if (!title) {
      title = 'Fleet Management';
    }
    
    // Extract subtitle/description - Priority: page.description > page-title section > default
    let subtitle = '';
    if (pageData.description) {
      subtitle = pageData.description;
    } else {
      subtitle = pageTitleSection.description || pageTitleSection.subtitle || 'Keep your business vehicles spotless and road-ready with our tailored fleet wash plans. Enjoy convenient scheduling, consistent quality, and cost savings designed for companies of any size.';
    }
    
    // Extract form theme
    const formTheme = formSection.theme || formSection.formTheme || 'light';
    
    // Extract button text
    const buttonText = formSection.buttonText || formSection.button?.text || 'Submit';
    
    // Extract background color - Priority: page.theme > form_section > page-title section > default
    let backgroundColor = '#FFFFFF';
    if (pageData.theme && pageData.theme.background_color) {
      backgroundColor = pageData.theme.background_color;
    } else {
      backgroundColor = formSection.backgroundColor || pageTitleSection.backgroundColor || '#FFFFFF';
    }
    
    // Initialize with all data
    ctx.fleetsPanelData = {
      title: title,
      subtitle: subtitle,
      titleStyle: titleStyle || undefined,
      formTheme: formTheme,
      buttonText: buttonText,
      backgroundColor: backgroundColor
    };
    
    this.logger.debug('🚛 Loaded fleets data from page-level:', {
      title: ctx.fleetsPanelData.title,
      subtitle: ctx.fleetsPanelData.subtitle,
      formTheme: ctx.fleetsPanelData.formTheme,
      backgroundColor: ctx.fleetsPanelData.backgroundColor
    });
  }

  /**
  * Updates fleets panel data and synchronizes page-level and section-level JSON state when the fleets panel is open.
  * @example
  * nFleetsChange(data, ctx)
  * void
  * @param {FleetsPanelData} data - The updated fleets panel data to apply.
  * @param {SectionContext} ctx - The section context containing panel state, JSON data, and autosave handling.
  * @returns {void} Does not return a value.
  **/
  onFleetsChange(data: FleetsPanelData, ctx: SectionContext): void {
    // Only process changes when the panel is actually open
    if (!ctx.showFleetsPanel) {
      return;
    }
    
    ctx.fleetsPanelData = data;
    
    // Update currentJsonData for API sync (live update)
    if (!ctx.json) ctx.json = {};
    if (!ctx.json.page) ctx.json.page = {};
    if (!ctx.json.page.sections) ctx.json.page.sections = {};
    
    // CRITICAL: Update page-level title and description (like wash packages)
    // Update page-level title with style
    if (data.titleStyle) {
      const existingPageTitle = ctx.json.page.title;
      if (typeof existingPageTitle === 'string') {
        ctx.json.page.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else if (existingPageTitle && typeof existingPageTitle === 'object') {
        ctx.json.page.title = {
          ...existingPageTitle,
          label: data.title || '',
          style: {
            ...(existingPageTitle.style || {}),
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else {
        ctx.json.page.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      }
    } else if (data.title) {
      ctx.json.page.title = data.title;
    }
    if (data.subtitle) {
      ctx.json.page.description = data.subtitle;
    }
    
    // Update page-level theme object (fleets may not have theme, but we'll set defaults)
    if (!ctx.json.page.theme) {
      ctx.json.page.theme = {};
    }
    ctx.json.page.theme.primary_color = '#1E40AF';
    ctx.json.page.theme.secondary_color = '#FFFFFF';
    ctx.json.page.theme.background_color = data.backgroundColor || '#F9FAFB';
    if (!ctx.json.page.theme.font_family) {
      ctx.json.page.theme.font_family = 'Inter, sans-serif';
    }
    
    // Update page-title section (NOT hero_section - fleets uses page-title)
    const pageTitleKey = 'page-title';
    if (!ctx.json.page.sections[pageTitleKey]) {
      ctx.json.page.sections[pageTitleKey] = {};
    }
    const pageTitleSection = ctx.json.page.sections[pageTitleKey];
    
    // Update title with style (same as hero section)
    const existingTitle = pageTitleSection.title;
    if (data.titleStyle) {
      // Create or update title object with style
      if (typeof existingTitle === 'string') {
        pageTitleSection.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else if (existingTitle && typeof existingTitle === 'object') {
        pageTitleSection.title = {
          ...existingTitle,
          label: data.title || '',
          style: {
            ...(existingTitle.style || {}),
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else {
        pageTitleSection.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      }
    } else {
      // No style, just update text
      if (existingTitle && typeof existingTitle === 'object' && existingTitle.label !== undefined) {
        pageTitleSection.title = {
          ...existingTitle,
          label: data.title || ''
        };
      } else if (typeof existingTitle === 'string') {
        pageTitleSection.title = data.title || '';
      } else {
        pageTitleSection.title = {
          label: data.title || '',
          style: {}
        };
      }
    }
    
    // Update description/subtitle
    pageTitleSection.description = data.subtitle || '';
    
    // Keep active state if it exists
    if (pageTitleSection.active === undefined) {
      pageTitleSection.active = true;
    }
    
    // Update form_section with theme, button text, and background color
    const formSectionKey = 'form_section';
    if (!ctx.json.page.sections[formSectionKey]) {
      ctx.json.page.sections[formSectionKey] = {};
    }
    const formSection = ctx.json.page.sections[formSectionKey];
    formSection.theme = data.formTheme || 'light';
    formSection.formTheme = data.formTheme || 'light';
    formSection.buttonText = data.buttonText || 'Submit';
    formSection.backgroundColor = data.backgroundColor || '#FFFFFF';
    
    // Update button object if it exists
    if (!formSection.button) {
      formSection.button = {};
    }
    formSection.button.text = data.buttonText || 'Submit';
    
    // Schedule autosave - include page-level updates
    ctx.scheduleAutosave([
      '/page/title',
      '/page/description',
      '/page/theme',
      `/page/sections/${pageTitleKey}`,
      `/page/sections/${formSectionKey}`
    ]);
    
    this.logger.debug('🚛 Fleets panel change detected, updated page-level and section data');
    
    // Keep active state if it exists
    if (formSection.active === undefined) {
      formSection.active = true;
    }
    
    this.logger.debug('🚛 Fleets panel change detected, updated sections');
  }

  /**
   * Saves fleets panel data into the section context and updates related hero and form section JSON.
   * @example
   * nFleetsSave(data, ctx)
   * undefined
   * @param {FleetsPanelData} data - Fleets panel data containing title, subtitle, styles, theme, and button settings.
   * @param {SectionContext} ctx - Section context whose JSON and panel data will be updated.
   * @returns {void} No return value.
   **/
  onFleetsSave(data: FleetsPanelData, ctx: SectionContext): void {
    ctx.fleetsPanelData = data;
    
    // Update currentJsonData (same as onFleetsChange)
    if (!ctx.json) ctx.json = {};
    if (!ctx.json.page) ctx.json.page = {};
    if (!ctx.json.page.sections) ctx.json.page.sections = {};
    
    // Update hero_section with title and subtitle
    const heroSectionKey = 'hero_section';
    if (!ctx.json.page.sections[heroSectionKey]) {
      ctx.json.page.sections[heroSectionKey] = {};
    }
    const heroSection = ctx.json.page.sections[heroSectionKey];
    
    // Update title with style (same as hero section)
    const existingTitle = heroSection.title;
    if (data.titleStyle) {
      // Create or update title object with style
      if (typeof existingTitle === 'string') {
        heroSection.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else if (existingTitle && typeof existingTitle === 'object') {
        heroSection.title = {
          ...existingTitle,
          label: data.title || '',
          style: {
            ...(existingTitle.style || {}),
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      } else {
        heroSection.title = {
          label: data.title || '',
          style: {
            text_style: {
              fontWeight: data.titleStyle.fontWeight || null,
              fontStyle: data.titleStyle.fontStyle || null,
              textDecoration: data.titleStyle.textDecoration || null,
              textAlign: data.titleStyle.textAlign || null
            }
          }
        };
      }
    } else {
      // No style, just update text
      if (existingTitle && typeof existingTitle === 'object' && existingTitle.label !== undefined) {
        heroSection.title = {
          ...existingTitle,
          label: data.title || ''
        };
      } else if (typeof existingTitle === 'string') {
        heroSection.title = data.title || '';
      } else {
        heroSection.title = {
          label: data.title || '',
          style: {}
        };
      }
    }
    
    // Update description/subtitle
    heroSection.description = data.subtitle || '';
    
    // Keep active state if it exists
    if (heroSection.active === undefined) {
      heroSection.active = true;
    }
    
    // Update form_section with theme, button text, and background color
    const formSectionKey = 'form_section';
    if (!ctx.json.page.sections[formSectionKey]) {
      ctx.json.page.sections[formSectionKey] = {};
    }
    const formSection = ctx.json.page.sections[formSectionKey];
    formSection.theme = data.formTheme || 'light';
    formSection.formTheme = data.formTheme || 'light';
    formSection.buttonText = data.buttonText || 'Submit';
    formSection.backgroundColor = data.backgroundColor || '#FFFFFF';
    
    // Update button object if it exists
    if (!formSection.button) {
      formSection.button = {};
    }
    formSection.button.text = data.buttonText || 'Submit';
    
    // Keep active state if it exists
    if (formSection.active === undefined) {
      formSection.active = true;
    }
    
    // Trigger autosave
    ctx.scheduleAutosave([`/page/sections/${heroSectionKey}`, `/page/sections/${formSectionKey}`]);
    
    this.logger.debug('💾 Fleets panel SAVE triggered:', data);
  }
}